import { ShieldUser, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/auth/AuthContext';
import { AccountActions } from '@/components/Accounts/AccountActions';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useAdmins, useAllInvites, useAllPartnerUsers } from '@/hooks/useAccounts';
import { usePartners } from '@/hooks/usePartners';
import { isInviteExpired, type InviteType } from '@/types/types_invite';
import { partnerRoleLabels, type PartnerUserType } from '@/types/types_partnerUser';
import { callableErrorMessage, revokePartnerInvite } from '@/util/callable_functions';
import { formatDate } from '@/util/util_format';

const th =
  'tw:px-6 tw:py-3 tw:text-left tw:text-xs tw:font-medium tw:tracking-wider tw:text-ed-stone tw:uppercase';
const pill = 'tw:rounded-full tw:px-2 tw:py-1 tw:text-xs';

/**
 * Everyone who can sign in, in one place.
 *
 * The per-partner view on the partner edit page answers "who works for this
 * partner"; this answers the security question that one can't — "who has access
 * to this system at all" — without walking every partner in turn. Issuing and
 * regenerating invitation links stays there, because a link belongs to the
 * partner you were already looking at; revoking one is here too, since a live
 * link is exactly the kind of standing access this page exists to show.
 */
export function PageAccounts() {
  const { user } = useAuth();
  const { admins, error: adminsError } = useAdmins();
  const { partnerUsers, loading, error: usersError } = useAllPartnerUsers();
  const { invites, error: invitesError } = useAllInvites();
  const { partners } = usePartners();

  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [toRevoke, setToRevoke] = useState<InviteType | null>(null);
  const [busy, setBusy] = useState(false);

  const partnerNames = useMemo(() => new Map(partners.map((p) => [p.id, p.name])), [partners]);

  // Both of these feed the memos below, so they have to be stable across
  // renders — otherwise the filtered lists rebuild on every keystroke anywhere.
  const partnerName = useCallback(
    (partnerId: string) => partnerNames.get(partnerId) ?? partnerId,
    [partnerNames],
  );

  const matches = useCallback(
    (haystack: string[]) => {
      const needle = search.trim().toLowerCase();
      if (!needle) return true;
      return haystack.some((s) => s.toLowerCase().includes(needle));
    },
    [search],
  );

  // Whoever is currently each partner's manager, so a staff account's
  // "Make manager" button can be disabled with a reason rather than failing.
  const ownerByPartner = useMemo(() => {
    const map = new Map<string, PartnerUserType>();
    for (const u of partnerUsers) if (u.role === 'owner') map.set(u.partnerId, u);
    return map;
  }, [partnerUsers]);

  const accounts = useMemo(() => {
    return [...partnerUsers]
      .filter((u) => matches([u.displayName, u.email, partnerName(u.partnerId)]))
      .sort((a, b) => {
        const byPartner = partnerName(a.partnerId).localeCompare(partnerName(b.partnerId));
        if (byPartner !== 0) return byPartner;
        if (a.role !== b.role) return a.role === 'owner' ? -1 : 1;
        return (a.displayName || a.email).localeCompare(b.displayName || b.email);
      });
  }, [partnerUsers, partnerName, matches]);

  const pendingInvites = useMemo(
    () =>
      invites
        .filter((i) => i.status === 'pending')
        .filter((i) => matches([i.email, partnerName(i.partnerId)])),
    [invites, partnerName, matches],
  );

  const activeCount = partnerUsers.filter((u) => u.status === 'active').length;
  const stats = [
    { label: 'Eighty Days admins', value: admins.length },
    { label: 'Active partner accounts', value: activeCount },
    { label: 'Suspended', value: partnerUsers.length - activeCount },
    { label: 'Outstanding invitations', value: invites.filter((i) => i.status === 'pending').length },
  ];

  async function confirmRevoke() {
    if (!toRevoke) return;
    setBusy(true);
    setError(null);
    try {
      await revokePartnerInvite({ inviteId: toRevoke.id });
      setToRevoke(null);
    } catch (e) {
      console.error('revokePartnerInvite failed', e);
      setError(callableErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const loadError = error ?? usersError ?? invitesError ?? adminsError;

  return (
    <div className='tw:mx-auto tw:max-w-6xl'>
      <header className='tw:mb-6'>
        <h1 className='tw:font-serif tw:text-2xl tw:text-ed-black'>Accounts</h1>
        <p className='tw:mt-1 tw:text-sm tw:text-ed-stone'>
          Everyone who can sign in to Eighty Days — our own administrators, our partners, and the
          invitations that would add more.
        </p>
      </header>

      <div className='tw:mb-8 tw:grid tw:gap-4 tw:sm:grid-cols-2 tw:lg:grid-cols-4'>
        {stats.map((stat) => (
          <div key={stat.label} className='tw:rounded-lg tw:border tw:bg-card tw:p-6'>
            <p className='tw:text-3xl tw:font-semibold'>{stat.value}</p>
            <p className='tw:mt-1 tw:text-sm tw:text-muted-foreground'>{stat.label}</p>
          </div>
        ))}
      </div>

      {loadError && (
        <p className='tw:mb-4 tw:rounded tw:border tw:border-ed-red/30 tw:bg-ed-red/5 tw:px-4 tw:py-2.5 tw:text-sm tw:text-ed-red'>
          {loadError}
        </p>
      )}

      {/* Eighty Days admins. Read-only by design — see types_admin.ts. */}
      <section className='tw:mb-8 tw:rounded-lg tw:border tw:bg-card tw:p-6'>
        <h2 className='tw:mb-1 tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase'>
          <ShieldUser className='tw:size-4' /> Eighty Days Administrators
        </h2>
        <p className='tw:mb-4 tw:text-sm tw:text-muted-foreground'>
          Full access to this CMS, including every partner&rsquo;s accounting details. Membership is managed in
          the Firebase console — deliberately, since a screen for granting this would be protected by nothing
          but the same privilege.
        </p>
        <ul className='tw:divide-y tw:divide-ed-umber/10'>
          {admins.map((admin) => (
            <li key={admin.id} className='tw:flex tw:items-center tw:gap-3 tw:py-2 tw:first:pt-0'>
              <span className='tw:text-sm'>{admin.email ?? admin.id}</span>
              {admin.id === user?.uid && <span className='tw:text-xs tw:text-ed-stone'>(you)</span>}
            </li>
          ))}
        </ul>
      </section>

      <div className='tw:mb-4 tw:flex tw:items-center tw:justify-between tw:gap-4'>
        <h2 className='tw:font-serif tw:text-xl tw:text-ed-black'>Partner accounts</h2>
        <Input
          value={search}
          placeholder='Search name, email or partner…'
          onChange={(e) => setSearch(e.target.value)}
          className='tw:max-w-64'
        />
      </div>

      <div className='tw:overflow-x-auto tw:rounded-sm tw:border tw:border-ed-umber/10 tw:bg-white tw:shadow-sm'>
        <table className='tw:w-full'>
          <thead className='tw:bg-ed-cream/50'>
            <tr>
              {['Person', 'Partner', 'Role', 'Status', 'Added', ''].map((h) => (
                <th key={h} className={th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className='tw:px-6 tw:py-10 tw:text-center tw:text-ed-stone'>
                  Loading…
                </td>
              </tr>
            ) : accounts.length === 0 ? (
              <tr>
                <td colSpan={6} className='tw:px-6 tw:py-10 tw:text-center tw:text-ed-stone'>
                  {partnerUsers.length === 0
                    ? 'No partner accounts yet. Invite one from a partner’s page.'
                    : 'No accounts match that search.'}
                </td>
              </tr>
            ) : (
              accounts.map((account) => {
                const owner = ownerByPartner.get(account.partnerId);
                return (
                  <tr key={account.id} className='tw:border-t tw:border-ed-umber/10 tw:hover:bg-ed-cream/20'>
                    <td className='tw:px-6 tw:py-4 tw:text-sm tw:font-medium tw:text-ed-black'>
                      {account.displayName}
                      <div className='tw:text-xs tw:font-normal tw:text-ed-stone'>{account.email}</div>
                    </td>
                    <td className='tw:px-6 tw:py-4 tw:text-sm'>
                      <Link
                        to={`/admin/partners/${account.partnerId}/edit`}
                        className='tw:underline tw:underline-offset-4 tw:hover:text-ed-gold'
                      >
                        {partnerName(account.partnerId)}
                      </Link>
                    </td>
                    <td className='tw:px-6 tw:py-4 tw:text-sm tw:text-ed-stone'>
                      {partnerRoleLabels[account.role]}
                    </td>
                    <td className='tw:px-6 tw:py-4'>
                      <span
                        className={`${pill} ${
                          account.status === 'active'
                            ? 'tw:bg-green-100 tw:text-green-800'
                            : 'tw:bg-ed-umber/10 tw:text-ed-stone'
                        }`}
                      >
                        {account.status === 'active' ? 'Active' : 'Suspended'}
                      </span>
                    </td>
                    <td className='tw:px-6 tw:py-4 tw:text-sm tw:text-ed-stone'>
                      {formatDate(account.createdAt)}
                    </td>
                    <td className='tw:px-6 tw:py-4'>
                      <AccountActions
                        account={account}
                        mode='admin'
                        partnerName={partnerName(account.partnerId)}
                        hasOtherOwner={!!owner && owner.id !== account.id}
                        onError={setError}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <h2 className='tw:mt-10 tw:mb-4 tw:font-serif tw:text-xl tw:text-ed-black'>Outstanding invitations</h2>
      <div className='tw:overflow-x-auto tw:rounded-sm tw:border tw:border-ed-umber/10 tw:bg-white tw:shadow-sm'>
        <table className='tw:w-full'>
          <thead className='tw:bg-ed-cream/50'>
            <tr>
              {['Email', 'Partner', 'Role', 'Expires', 'Invited by', ''].map((h) => (
                <th key={h} className={th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pendingInvites.length === 0 ? (
              <tr>
                <td colSpan={6} className='tw:px-6 tw:py-10 tw:text-center tw:text-ed-stone'>
                  No invitations are outstanding.
                </td>
              </tr>
            ) : (
              pendingInvites.map((invite) => (
                <tr key={invite.id} className='tw:border-t tw:border-ed-umber/10 tw:hover:bg-ed-cream/20'>
                  <td className='tw:px-6 tw:py-4 tw:text-sm'>{invite.email}</td>
                  <td className='tw:px-6 tw:py-4 tw:text-sm'>
                    <Link
                      to={`/admin/partners/${invite.partnerId}/edit`}
                      className='tw:underline tw:underline-offset-4 tw:hover:text-ed-gold'
                    >
                      {partnerName(invite.partnerId)}
                    </Link>
                  </td>
                  <td className='tw:px-6 tw:py-4 tw:text-sm tw:text-ed-stone'>
                    {partnerRoleLabels[invite.role]}
                  </td>
                  <td className='tw:px-6 tw:py-4 tw:text-sm'>
                    {isInviteExpired(invite) ? (
                      <span className={`${pill} tw:bg-ed-umber/10 tw:text-ed-stone`}>
                        Expired {formatDate(invite.expiresAt)}
                      </span>
                    ) : (
                      <span className='tw:text-ed-stone'>{formatDate(invite.expiresAt)}</span>
                    )}
                  </td>
                  <td className='tw:px-6 tw:py-4 tw:text-xs tw:text-ed-stone'>{invite.createdByEmail}</td>
                  <td className='tw:px-6 tw:py-4'>
                    {/* Revoke only. Issuing and regenerating live on the partner
                        page, which is where the one-time link can be shown in
                        the context of the partner it belongs to. */}
                    <div className='tw:flex tw:justify-end'>
                      <Button
                        type='button'
                        size='xs'
                        variant='destructive'
                        disabled={busy}
                        onClick={() => setToRevoke(invite)}
                      >
                        <X /> Revoke
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!toRevoke} onOpenChange={(open) => !open && !busy && setToRevoke(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke invitation?</DialogTitle>
            <DialogDescription>
              The link sent to {toRevoke?.email} for {toRevoke && partnerName(toRevoke.partnerId)} will stop
              working immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' disabled={busy} onClick={() => setToRevoke(null)}>
              Cancel
            </Button>
            <Button variant='destructive' loading={busy} onClick={() => void confirmRevoke()}>
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
